import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { User } from "@core/models/user.model";
import { AuthService } from "@core/services/auth.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs";

import * as AuthActions from "./auth.actions";
import { SELECT_MFA_TARGET, SELECT_MFA_TOKEN } from "./auth.selector";

@Injectable()
export class AuthEffects {
  getLogin$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.DO_INITIAL_LOGIN),
      // No active session on startup is the normal "please log in" state, not a
      // failed login attempt — surfacing it would wrongly show a login error on
      // the login page before the user has even tried.
      filter((): boolean => this.authService.isAuthenticated),
      map((): Action => AuthActions.LOAD_USER_DATA())
    );
  });

  login$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.DO_LOGIN),

      switchMap(({ credentials, target }) => {
        return this.authService.login$(credentials).pipe(
          switchMap((response) => {
            if (this.authService.isMfaRequired(response)) {
              return of(
                AuthActions.LOGIN_MFA_REQUIRED({
                  mfaToken: response.mfaToken,
                  target,
                })
              );
            }

            return this.authService.getUserData$().pipe(
              tap((): void => {
                void this.router.navigate([target]);
              }),

              map((user: User): Action => {
                return AuthActions.LOGIN_SUCCESS({ user });
              })
            );
          }),

          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.LOGIN_FAILED({ error }));
          })
        );
      })
    );
  });

  // Once the password step reports 2FA is required, look up whether the user already has an
  // authenticator: enrolled users are challenged for a code, others are sent through enrollment.
  setupMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.LOGIN_MFA_REQUIRED),

      switchMap(({ mfaToken }) => {
        return this.authService.setup2fa$(mfaToken).pipe(
          map((setup): Action => {
            return AuthActions.MFA_SETUP_LOADED({
              enrolled: setup.enrolled,
              sharedKey: setup.sharedKey,
              authenticatorUri: setup.authenticatorUri,
            });
          }),

          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.MFA_FAILED({ error }));
          })
        );
      })
    );
  });

  // Challenge: an enrolled user submits the code from their authenticator app.
  verifyMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.VERIFY_MFA),

      withLatestFrom(
        this.store.select(SELECT_MFA_TOKEN),
        this.store.select(SELECT_MFA_TARGET)
      ),

      switchMap(([{ code, rememberDevice }, mfaToken, target]) => {
        if (!mfaToken) {
          return of(AuthActions.CANCEL_MFA());
        }

        return this.authService.verify2fa$(mfaToken, code, rememberDevice).pipe(
          switchMap(() => this.completeLogin$(target)),
          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.MFA_FAILED({ error }));
          })
        );
      })
    );
  });

  // Enrollment: the user confirms setup with their first code; on success we get recovery codes.
  enableMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.ENABLE_MFA),

      withLatestFrom(this.store.select(SELECT_MFA_TOKEN)),

      switchMap(([{ code, rememberDevice }, mfaToken]) => {
        if (!mfaToken) {
          return of(AuthActions.CANCEL_MFA());
        }

        return this.authService.enable2fa$(mfaToken, code, rememberDevice).pipe(
          map((result): Action => {
            return AuthActions.MFA_ENABLED({
              recoveryCodes: result.recoveryCodes,
            });
          }),
          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.MFA_FAILED({ error }));
          })
        );
      })
    );
  });

  // After enrollment we hold the user on the recovery-codes screen; acknowledging finishes login.
  finalizeMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.MFA_RECOVERY_ACKNOWLEDGED),

      withLatestFrom(this.store.select(SELECT_MFA_TARGET)),

      switchMap(([, target]) => this.completeLogin$(target))
    );
  });

  // Recovery: complete login with a one-time recovery code instead of an app code.
  recoveryMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.USE_RECOVERY_CODE),

      withLatestFrom(
        this.store.select(SELECT_MFA_TOKEN),
        this.store.select(SELECT_MFA_TARGET)
      ),

      switchMap(([{ recoveryCode, rememberDevice }, mfaToken, target]) => {
        if (!mfaToken) {
          return of(AuthActions.CANCEL_MFA());
        }

        return this.authService
          .recovery2fa$(mfaToken, recoveryCode, rememberDevice)
          .pipe(
            switchMap(() => this.completeLogin$(target)),
            catchError((error: HttpErrorResponse): Observable<Action> => {
              return of(AuthActions.MFA_FAILED({ error }));
            })
          );
      })
    );
  });

  logout$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.DO_LOGOUT),

      switchMap(() => {
        return this.authService.logout$().pipe(
          tap((): void => {
            void this.router.navigate(["/login"]);
          }),

          map((): Action => {
            return AuthActions.LOGOUT_SUCCESS();
          }),

          catchError((): Observable<Action> => {
            this.authService.clearSession();
            void this.router.navigate(["/login"]);

            return of(AuthActions.LOGOUT_SUCCESS());
          })
        );
      })
    );
  });

  loadUserData$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.LOAD_USER_DATA),

      switchMap(() => {
        return this.authService.getUserData$().pipe(
          map((user: User): Action => {
            return AuthActions.SET_USER_DATA({ user });
          }),

          catchError((error: HttpErrorResponse): Observable<Action> => {
            this.authService.clearSession();

            return of(AuthActions.LOGIN_FAILED({ error }));
          })
        );
      })
    );
  });

  // Shared completion for the paths that hold real tokens (verify / recovery / finalize): load the
  // user and route to the originally-requested page.
  private completeLogin$(target: string | null): Observable<Action> {
    return this.authService.getUserData$().pipe(
      tap((): void => {
        void this.router.navigate([target ?? "/"]);
      }),
      map((user: User): Action => AuthActions.LOGIN_SUCCESS({ user })),
      catchError((error: HttpErrorResponse): Observable<Action> => {
        this.authService.clearSession();
        return of(AuthActions.LOGIN_FAILED({ error }));
      })
    );
  }

  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly store: Store
  ) {}
}
